import type { UseQueryResult } from "@tanstack/react-query";

import { Heading } from "@sentricmusic/ui";
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import type {
  GigClaim,
} from "@/api/queries";

import {
  useGetGigClaims,
  useGetMyAccount,
  useGetMyProfile,
} from "@/api/queries";
import useSiteConfigAuth from "@/api/sanity/queries/getSiteConfigWithCode";
import { sendProperty } from "@/helpers/helpers";
import useAppStore from "@/store/appStateZustand";

import { hasAdminRights } from "../../auth/shared";
import AllGigs from "./components/AllGigsTable";

function Gigs() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: siteConfig } = useSiteConfigAuth();
  const selectedAccountValue = useAppStore(s => s.selectedAccount);
  // Tightned selected account type
  const selectedAccount = typeof selectedAccountValue === "string" ? selectedAccountValue : "";
  const myAccount = useGetMyAccount({
    artistaccountcode: selectedAccount ?? "",
  });
  const accountData = myAccount.data;
  const gigClaims: UseQueryResult<GigClaim[], Error> = useGetGigClaims({
    ac: selectedAccount,
    oc: accountData?.originatorcode?.code,
  });
  const {
    data: gigClaimsData,
    isSuccess: isGigClaimsSuccess,
    refetch: refetchGigClaims,
  } = gigClaims;
  const profile = useGetMyProfile();
  const adminRights = hasAdminRights(accountData);
  const hubspotProps = useAppStore(s => s.hubspot);
  const trackedGigCount = hubspotProps?.songs as number | undefined;
  const gigCount = gigClaimsData?.length;
  const profileEmail = (profile.data)?.email;

  const noGigs = siteConfig?.disable_gigs;
  useEffect(() => {
    if (noGigs) {
      void navigate({ to: "/" });
    }
  }, [noGigs, navigate]);

  useEffect(() => {
    if (selectedAccount !== "") {
      void refetchGigClaims();
    }
  }, [selectedAccount, refetchGigClaims]);

  useEffect(() => {
    if (profile.isSuccess && isGigClaimsSuccess) {
      if (
        trackedGigCount == null
        || trackedGigCount !== gigCount
      ) {
        sendProperty({
          property: "gigs",
          value: gigCount,
          email: profileEmail,
        });
        useAppStore.setState(s => ({ hubspot: { ...s.hubspot, songs: gigCount } }));
      }
    }
  }, [profile.isSuccess, isGigClaimsSuccess, trackedGigCount, gigCount, profileEmail]);

  return (
    <div className="flex-1 order-last xl:w-full bg-layer-raised min-h-50 lg:min-h-screen-minus-header">
      <Heading as="h3" className="mb-6">{t("gigs:your-gigs-header")}</Heading>
      <div className="bg-white rounded-lg p-4 md:px-14 md:py-10 overflow-x-auto">
        <AllGigs gigClaims={gigClaims} adminRights={adminRights} />
      </div>
    </div>
  );
}
export default Gigs;
